/**
 * Which credential a telemetry batch should carry — the one rule shared by
 * logger.ts (fetch/sendBeacon) and tracing.ts (OTLP exporter headers), so the
 * two transports cannot drift apart.
 *
 * A plain truthiness check on the user JWT is not enough. The app stores the
 * access token in localStorage and only clears it once an API call comes back
 * 401 AND the refresh attempt also fails (src/services/api.ts). In the window
 * between the token expiring and that happening, `getToken()` still returns the
 * dead JWT. Sending it actively costs us the batch: the backend's `jwt.verify`
 * rejects it, and because an Authorization header WAS present the request never
 * falls through to the app-level ingest token, so the batch is 401'd
 * (nap-backend telemetry-auth.js). Session expiry is precisely when the
 * telemetry is most useful, so an expired JWT is treated as unusable and the
 * ingest token takes over.
 */

/** Decode a base64url segment. Returns undefined rather than throwing. */
function base64UrlDecode(segment: string): string | undefined {
  if (typeof atob !== 'function') return undefined;
  const b64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  try {
    return atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, '='));
  } catch {
    return undefined;
  }
}

/**
 * Read the `exp` claim (seconds since epoch) out of a JWT without verifying the
 * signature — the backend remains the authority on validity; here we only need
 * to know whether sending the token is worth the round trip. Returns undefined
 * when the value is not a decodable JWT or carries no numeric `exp`.
 */
function jwtExpSeconds(token: string): number | undefined {
  const parts = token.split('.');
  if (parts.length !== 3) return undefined;
  const json = base64UrlDecode(parts[1]);
  if (json === undefined) return undefined;
  try {
    const payload = JSON.parse(json) as { exp?: unknown };
    return typeof payload.exp === 'number' && Number.isFinite(payload.exp) ? payload.exp : undefined;
  } catch {
    return undefined;
  }
}

/**
 * True when a user JWT is worth sending. False for undefined/null, blank
 * strings, and JWTs whose `exp` has already passed. An opaque or non-JWT string
 * is accepted: we cannot prove it is dead, so we let the backend decide.
 */
export function isUsableToken(token: string | undefined | null): token is string {
  if (typeof token !== 'string' || token.trim() === '') return false;
  const exp = jwtExpSeconds(token);
  if (exp === undefined) return true;
  return exp * 1000 > Date.now();
}

/**
 * Pick the Authorization credential for a telemetry batch: the user JWT when it
 * is usable, otherwise the app-level ingest token, otherwise nothing (the
 * caller then omits the header entirely).
 *
 * Deliberately not `token ?? ingestToken` — `??` only falls through on
 * null/undefined, so a blank or expired JWT would win and suppress the fallback.
 */
export function resolveTelemetryAuth(token: string | undefined | null, ingestToken?: string): string | undefined {
  if (isUsableToken(token)) return token;
  return ingestToken ? ingestToken : undefined;
}
